import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { useStore } from 'effector-react';
import cn from 'classnames';
import featuring, { getList } from '@stores/featuring';
import useScrollTopPage from '@hooks/useScrollTopPage';
import { Spin, Pagination, Button, Radio } from 'antd';
import css from './FeaturingList.module.less';
import FeaturingPost from '@pages/FeaturingPage/FeaturingPost/FeaturingPost';
import {
  PlusOutlined,
  SortDescendingOutlined,
  SortAscendingOutlined,
  ArrowDownOutlined,
} from '@ant-design/icons';
import { openModal } from '@stores/modals';
import useTranslate from '@hooks/useTranslate';
import componentContent from '../FeaturingPage.module.json';
import useGuards from '@hooks/useGuards';
import { RadioChangeEvent } from 'antd/lib/radio';
import { IFeaturing } from '@interfaces/IFeaturing';
import IGame from '@interfaces/IGame';
import toCsvParser, { ITableParam } from '@helpers/toCsvParser';
import saveAs from '@helpers/fileSaver';

enum FeaturingTypes {
  ACTIVE = 'TRUE',
  FINISHED = 'FALSE',
}

const FeaturingList: FunctionComponent<unknown> = () => {
  const content = useTranslate(componentContent);
  const { list, preloaders, pagination } = useStore(featuring);
  const { allowChange } = useGuards();

  const [sortType, setSortType] = useState<string>('desc');

  const [typeFilter, setTypeFilter] = useState<string>(FeaturingTypes.ACTIVE);

  const paginationChange = useCallback(
    (current?: number, pageSize?: number) => {
      localStorage.setItem('featuringPageSize', `${pageSize}`);
      getList({
        pagination: { current, pageSize },
        active: typeFilter,
      });
    },
    [typeFilter],
  );

  const createFeaturing = useCallback(() => {
    openModal({ name: 'featuring' });
  }, []);

  const changeSortFilter = () => {
    if (sortType === 'desc') {
      setSortType('asc');
    } else {
      setSortType('desc');
    }
  };

  const changeTypeFilter = (event: RadioChangeEvent) => {
    setTypeFilter(event.target.value);
  };

  useEffect(() => {
    getList({
      pagination: {
        current: 1,
      },
      sortType,
      active: typeFilter,
    });
  }, [sortType, typeFilter]);

  const downloadReport = () => {
    const tableParams: ITableParam[] = [
      {
        title: 'Post ID',
        key: ['post', 'id'],
      },
      {
        title: 'Locale',
        key: 'locale',
        valueRule: (value: string) => value || 'ALL',
      },
      {
        title: 'Game',
        key: 'games',
        valueRule: (value: IGame[]) => (value.length === 0 ? 'ALL' : value[0]['name']),
      },
      {
        title: 'Start date',
        key: 'startDate',
      },
      {
        title: 'Finish date',
        key: 'endDate',
      },
      {
        title: 'Views',
        key: ['post', 'viewsCount'],
      },
      {
        title: 'Likes',
        key: ['post', 'likesCount'],
      },
      {
        title: 'Comments',
        key: ['post', 'commentsCount'],
      },
    ];
    const parsedTable: string = toCsvParser<IFeaturing>(tableParams, list);
    saveAs(parsedTable, 'featuring-posts.csv');
  };

  useScrollTopPage(pagination);
  return (
    <>
      <div className={cn(css.paginationContainer, css.top)}>
        <div>
          {allowChange ? (
            <Button
              type='primary'
              icon={<PlusOutlined />}
              onClick={createFeaturing}
              className={css.headerButton}
            >
              {content.add_featuring}
            </Button>
          ) : null}
          <Button
            type='primary'
            icon={<ArrowDownOutlined />}
            onClick={downloadReport}
            className={css.headerButton}
          >
            {content.download_report}
          </Button>
        </div>
        <Pagination
          {...pagination}
          showSizeChanger
          showQuickJumper
          onChange={paginationChange}
        />
      </div>
      <div className={cn(css.paginationContainer, css.top)}>
        <Radio.Group
          onChange={changeTypeFilter}
          value={typeFilter || undefined}
        >
          <Radio.Button value={FeaturingTypes.ACTIVE}>{content.active}</Radio.Button>
          <Radio.Button value={FeaturingTypes.FINISHED}>{content.finished}</Radio.Button>
        </Radio.Group>
        <Button
          icon={sortType === 'desc' ? <SortDescendingOutlined /> : <SortAscendingOutlined />}
          onClick={changeSortFilter}
          shape='circle'
        />
      </div>
      <Spin spinning={!!preloaders.getList}>
        <div>
          {list.map((featuring) => (
            <FeaturingPost
              key={featuring.id}
              featuring={featuring}
            />
          ))}
        </div>
      </Spin>
      <div className={css.paginationContainer}>
        <Pagination
          {...pagination}
          showSizeChanger
          showQuickJumper
          onChange={paginationChange}
        />
      </div>
    </>
  );
};

export default FeaturingList;
